import { useState } from 'react';
import { Modal, ModalCloseButton, ModalProps, ModalTitle, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { faRotate } from '@fortawesome/pro-regular-svg-icons';
import { faFire, faBoltLightning, faAward, faDoorClosed, faTrophy } from '@fortawesome/pro-solid-svg-icons';
import { StyledBadgeImageWrapper, StyledCodeTag, StyledFooterWrapper, StyledH3, StyledH3SubTitle, StyledIconButton, StyledIconsLegend, StyledIframe, StyledImageWrapper, StyledLegendIcon, StyledLegendIconsWrapper, StyledLegendText, StyledModalFooter, StyledPDescription, StyledRotateIcon } from 'src/features/public-profile/components/public-profile-header/components/profile-badge-modal/profile-badge-modal.styles';
import { usePutPublicBadgeImageMutation } from 'src/features/public-profile/public-profile.slice';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
export type ProfileBadgeModalProps = ModalProps & {
  userPublicId: number;
  badgeImageURL: string;
  username: string;
};
export const legendTypes = ([{
  type: 'level',
  icon: faBoltLightning
}, {
  type: 'rank',
  icon: faTrophy
}, {
  type: 'badges',
  icon: faAward
}, {
  type: 'streak',
  icon: faFire
}, {
  type: 'completed rooms',
  icon: faDoorClosed
}] as const);
const genOriginURL = () => process.env.NODE_ENV === 'development' ? 'http://localhost:1337' : window.location.origin.toString();
export const ProfileBadgeModal: React.FC<ProfileBadgeModalProps> = ({
  userPublicId,
  username,
  badgeImageURL,
  ...modalProps
}) => {
  const [imageUpdateKey, setImageUpdateKey] = useState(0);
  const [generatePublicBadgeImageQuery, {
    isLoading
  }] = usePutPublicBadgeImageMutation();
  const generateBadgeImageURL = async () => {
    try {
      await generatePublicBadgeImageQuery({
        userPublicId,
        username
      }).unwrap();
      setImageUpdateKey(prev => prev + 1);
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'Something went wrong.');
    }
  };
  return <Modal {...modalProps} minHeight="70rem" data-sentry-element="Modal" data-sentry-component="ProfileBadgeModal" data-sentry-source-file="profile-badge-modal.tsx">
      <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="profile-badge-modal.tsx">Your TryHackMe badge</ModalTitle>
      <StyledH3 data-sentry-element="StyledH3" data-sentry-source-file="profile-badge-modal.tsx">Badge</StyledH3>
      <StyledPDescription data-sentry-element="StyledPDescription" data-sentry-source-file="profile-badge-modal.tsx">
        This is your THM badge which comes with live updates as your streak grows and you complete more rooms. Include
        the following script to the web page you want your badge on!
      </StyledPDescription>
      <StyledCodeTag data-sentry-element="StyledCodeTag" data-sentry-source-file="profile-badge-modal.tsx">
        {`<iframe src="${genOriginURL()}/api/v2/badges/public-profile?userPublicId=${userPublicId}" style='border:none;'></iframe>`}
      </StyledCodeTag>
      {/* TODO [2024-12-28]: Add Skeleton for loading scene */}
      <StyledIframe title="public-badge" src={`${genOriginURL()}/api/v2/badges/public-profile?userPublicId=${userPublicId}`} data-sentry-element="StyledIframe" data-sentry-source-file="profile-badge-modal.tsx" />
      <StyledH3SubTitle data-sentry-element="StyledH3SubTitle" data-sentry-source-file="profile-badge-modal.tsx">Static image</StyledH3SubTitle>
      <StyledPDescription data-sentry-element="StyledPDescription" data-sentry-source-file="profile-badge-modal.tsx">You can also include a static image of your badge.</StyledPDescription>
      {badgeImageURL ? <>
          <StyledCodeTag>{`<img src="${badgeImageURL}" alt="Your Image Badge" />`}</StyledCodeTag>
          <StyledBadgeImageWrapper>
            <StyledImageWrapper>
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img src={`${badgeImageURL}?update=${imageUpdateKey}`} alt="Your Image Badge" />
            </StyledImageWrapper>
            <StyledIconButton color="secondary" variant="outlined"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={generateBadgeImageURL} disabled={isLoading} isLoading={isLoading}>
              <StyledRotateIcon icon={faRotate} />
              Regenerate badge image
            </StyledIconButton>
          </StyledBadgeImageWrapper>
        </> : <StyledPDescription>Loading...</StyledPDescription>}
      <StyledModalFooter data-sentry-element="StyledModalFooter" data-sentry-source-file="profile-badge-modal.tsx">
        <StyledFooterWrapper data-sentry-element="StyledFooterWrapper" data-sentry-source-file="profile-badge-modal.tsx">
          <StyledIconsLegend data-sentry-element="StyledIconsLegend" data-sentry-source-file="profile-badge-modal.tsx">
            <StyledLegendText $isBolder data-sentry-element="StyledLegendText" data-sentry-source-file="profile-badge-modal.tsx">Key:</StyledLegendText>
            {legendTypes.map(legend => <StyledLegendIconsWrapper key={legend.type}>
                <StyledLegendIcon icon={legend.icon} $legendType={legend.type} />
                <StyledLegendText>{legend.type}</StyledLegendText>
              </StyledLegendIconsWrapper>)}
          </StyledIconsLegend>
          <ModalCloseButton text="Close" data-sentry-element="ModalCloseButton" data-sentry-source-file="profile-badge-modal.tsx" />
        </StyledFooterWrapper>
      </StyledModalFooter>
    </Modal>;
};